import { useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Divider, Skeleton, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import {
  comparePlans,
  formatExactPrice,
  formatPrice,
  formatShortDate,
  pluralize,
} from "./billingFormat";

const RECOMMENDED_PLAN = "professional";

const planFeatures = (plan) =>
  [
    `Hasta ${plan.max_clients} clientes`,
    pluralize(plan.included_users, "usuario incluido", "usuarios incluidos"),
    plan.client_reporting ? "informes y métricas para clientes" : "sin informes para clientes",
    plan.white_label ? "marca blanca" : "sin marca blanca",
  ].join(" · ");

const PlanOption = ({ plan, selected, tag, onSelect }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 2,
        width: "100%",
        px: 2.5,
        py: 2,
        borderRadius: "12px",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${selected ? primary : theme.palette.divider}`,
        boxShadow: selected ? `0 0 0 1px ${primary}` : "none",
        transition: "border-color 0.15s, box-shadow 0.15s",
        "&:hover": { borderColor: selected ? primary : alpha(primary, 0.5) },
        "&:focus-visible": { outline: `2px solid ${primary}`, outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          flexShrink: 0,
          borderRadius: "50%",
          border: `1.5px solid ${selected ? primary : theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {selected && <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: primary }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>{plan.name}</Typography>
          {tag && (
            <Typography
              sx={{ fontSize: "12px", fontWeight: tag.accent ? 600 : 500, color: tag.accent ? primary : theme.palette.text.secondary }}
            >
              {tag.label}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "13px" }}>
          {planFeatures(plan)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexShrink: 0 }}>
        {plan.price ? (
          <>
            <Typography sx={{ fontWeight: 750, fontSize: "1.375rem", lineHeight: 1 }}>{formatPrice(plan.price)}</Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "12px" }}>/mes</Typography>
          </>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Próximamente</Typography>
        )}
      </Box>
    </Box>
  );
};

PlanOption.propTypes = {
  plan: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  tag: PropTypes.shape({ label: PropTypes.string, accent: PropTypes.bool }),
  onSelect: PropTypes.func.isRequired,
};

const DisabledWithTooltip = ({ tooltip, children }) => (
  <Tooltip title={tooltip} arrow>
    <span style={{ display: "block", width: "100%" }}>{children}</span>
  </Tooltip>
);

DisabledWithTooltip.propTypes = { tooltip: PropTypes.string, children: PropTypes.node };

const SummaryRow = ({ label, now, next, highlight }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr 1fr",
        alignItems: "center",
        py: 1.25,
        borderTop: `1px solid ${theme.palette.divider}`,
        fontSize: "13px",
      }}
    >
      <Typography sx={{ fontSize: "inherit", color: theme.palette.text.secondary }}>{label}</Typography>
      <Typography sx={{ fontSize: "inherit" }}>{now}</Typography>
      <Typography
        sx={{
          fontSize: "inherit",
          fontWeight: highlight ? 600 : 400,
          color: highlight ? theme.palette.primary.main : theme.palette.text.primary,
        }}
      >
        {next}
      </Typography>
    </Box>
  );
};

SummaryRow.propTypes = { label: PropTypes.string, now: PropTypes.node, next: PropTypes.node, highlight: PropTypes.bool };

/**
 * Cambio de plan: lista de planes a la izquierda y, a la derecha, el elegido frente al actual con el total
 * y la acción (Checkout sin suscripción; con ella, el diálogo de cambio).
 */
export const PlanPicker = ({ plans, billing, busy, disabled, onCheckout, onChangePlan }) => {
  const theme = useTheme();
  const isTrial = Boolean(billing.is_trial);
  const hasSubscription = Boolean(billing.has_subscription);
  const pastDue = Boolean(billing.past_due);
  // En prueba se compara con Free, que es a lo que pasa si no contrata
  const basePlanId = isTrial ? "free" : billing.plan;
  const currentPlanId = isTrial ? null : billing.plan;
  const extraUsers = billing.extra_users;

  const defaultId =
    currentPlanId ??
    plans?.find((plan) => plan.id === RECOMMENDED_PLAN && comparePlans(basePlanId, plan.id) > 0)?.id ??
    plans?.find((plan) => comparePlans(basePlanId, plan.id) > 0 && plan.price)?.id ??
    basePlanId;
  const [selectedId, setSelectedId] = useState(null);

  const tagFor = (plan) => {
    if (plan.id === currentPlanId) return { label: "Plan actual" };
    if (billing.downgrade_at_period_end && billing.scheduled_plan === plan.id) {
      return { label: `Desde el ${formatShortDate(billing.current_period_end)}` };
    }
    if (isTrial && plan.id === "free") return { label: "Al terminar la prueba" };
    if (plan.id === RECOMMENDED_PLAN && comparePlans(basePlanId, plan.id) > 0) return { label: "Recomendado", accent: true };
    return null;
  };

  const selected = plans?.find((plan) => plan.id === (selectedId ?? defaultId));
  const basePlan = plans?.find((plan) => plan.id === basePlanId);

  const renderAction = () => {
    const isCurrent = selected.id === currentPlanId;
    const anyBusy = Boolean(busy);
    const pastDueTooltip = "Resuelve el pago pendiente antes de cambiar de plan.";
    if (isCurrent) {
      return (
        <Button variant="outline" fullWidth disabled>
          Es tu plan actual
        </Button>
      );
    }
    if (billing.downgrade_at_period_end && billing.scheduled_plan === selected.id) {
      return (
        <Button variant="outline" fullWidth disabled>
          Pasas a {selected.name} el {formatShortDate(billing.current_period_end)}
        </Button>
      );
    }
    if (selected.id === "free") {
      if (billing.cancel_at_period_end) {
        return (
          <Button variant="outline" fullWidth disabled>
            Pasas a Free el {formatShortDate(billing.current_period_end)}
          </Button>
        );
      }
      if (!hasSubscription) {
        return (
          <Button variant="outline" fullWidth disabled>
            {isTrial ? "Tu plan al terminar la prueba" : "Es tu plan actual"}
          </Button>
        );
      }
      const button = (
        <Button variant="outline" fullWidth disabled={pastDue || anyBusy || disabled} onClick={() => onChangePlan(selected)}>
          Pasar a Free
        </Button>
      );
      return pastDue ? <DisabledWithTooltip tooltip={pastDueTooltip}>{button}</DisabledWithTooltip> : button;
    }
    if (!selected.price) {
      return (
        <DisabledWithTooltip tooltip="Este plan estará disponible muy pronto.">
          <Button variant="outline" fullWidth disabled>
            Próximamente
          </Button>
        </DisabledWithTooltip>
      );
    }
    const label = hasSubscription ? `Cambiar a ${selected.name}` : `Contratar ${selected.name}`;
    if (pastDue) {
      return (
        <DisabledWithTooltip tooltip={pastDueTooltip}>
          <Button fullWidth disabled>
            {label}
          </Button>
        </DisabledWithTooltip>
      );
    }
    const redirecting = busy?.kind === "checkout" && busy.plan === selected.id;
    return (
      <Button
        fullWidth
        variant={hasSubscription && comparePlans(billing.plan, selected.id) < 0 ? "outline" : "default"}
        disabled={anyBusy || disabled}
        onClick={() => (hasSubscription ? onChangePlan(selected) : onCheckout(selected))}
        sx={{ gap: 1 }}
      >
        {redirecting ? (
          <>
            <CircularProgress size={16} color="inherit" /> Redirigiendo…
          </>
        ) : (
          label
        )}
      </Button>
    );
  };

  const renderSummary = () => {
    if (!selected) return <Skeleton variant="rounded" height={320} />;
    const users = (plan) => plan.included_users + extraUsers;
    const yesNo = (value) => (value ? "Sí" : "No");
    return (
      <Card sx={{ p: 3, gap: 0, position: { md: "sticky" }, top: { md: 24 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.25rem" }}>{selected.name}</Typography>
          {basePlan && selected.id !== basePlan.id && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: "13px" }}>
              frente a {basePlan.name}
            </Typography>
          )}
        </Box>
        {basePlan && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1fr 1fr",
                pb: 1,
                fontSize: "12px",
                color: theme.palette.text.secondary,
              }}
            >
              <span />
              <Typography sx={{ fontSize: "inherit" }}>{isTrial ? basePlan.name : "Ahora"}</Typography>
              <Typography sx={{ fontSize: "inherit" }}>Con {selected.name}</Typography>
            </Box>
            <SummaryRow
              label="Clientes"
              now={basePlan.max_clients}
              next={selected.max_clients}
              highlight={selected.max_clients > basePlan.max_clients}
            />
            <SummaryRow
              label="Usuarios"
              now={users(basePlan)}
              next={users(selected)}
              highlight={selected.included_users > basePlan.included_users}
            />
            <SummaryRow
              label="Informes y métricas para clientes"
              now={yesNo(basePlan.client_reporting)}
              next={yesNo(selected.client_reporting)}
              highlight={selected.client_reporting && !basePlan.client_reporting}
            />
            <SummaryRow
              label="Marca blanca"
              now={yesNo(basePlan.white_label)}
              next={yesNo(selected.white_label)}
              highlight={selected.white_label && !basePlan.white_label}
            />
          </>
        )}
        {selected.price?.amount > 0 && (
          <>
            <Divider sx={{ mt: 1, mb: 2 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2.5 }}>
              <Typography sx={{ fontWeight: 700 }}>Precio al mes</Typography>
              <Typography sx={{ fontWeight: 700 }}>{formatExactPrice(selected.price)}</Typography>
            </Box>
          </>
        )}
        {!(selected.price?.amount > 0) && <Box sx={{ mt: 2.5 }} />}
        {renderAction()}
      </Card>
    );
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.6fr) minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 0.5 }}>Cambia de plan</Typography>
        <Box role="radiogroup" aria-label="Planes" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {plans
            ? plans.map((plan) => (
              <PlanOption
                key={plan.id}
                plan={plan}
                selected={plan.id === selected?.id}
                tag={tagFor(plan)}
                onSelect={() => setSelectedId(plan.id)}
              />
            ))
            : [0, 1, 2, 3].map((index) => <Skeleton key={index} variant="rounded" height={72} />)}
        </Box>
      </Box>
      {renderSummary()}
    </Box>
  );
};

PlanPicker.propTypes = {
  plans: PropTypes.arrayOf(PropTypes.object),
  billing: PropTypes.object.isRequired,
  busy: PropTypes.shape({ kind: PropTypes.string, plan: PropTypes.string }),
  disabled: PropTypes.bool,
  onCheckout: PropTypes.func.isRequired,
  onChangePlan: PropTypes.func.isRequired,
};
